import { useState } from 'react';
import { Modal } from '@shared-ui/components';
import { useLogger, WebComponentCode } from '@shared-ui/logging';
import type { QuestionDiffResult, QuestionDiffItem } from '@padcanvas/shared';
import { questionSetApi, type TestImportResult } from '../../api/questionSetApi';

// Import public-test DOCS exported from another environment. Questions are NOT
// imported here — a test whose questions are missing in this environment lands
// in the "errors" bucket (run the Questions-page import first). Dry-run preview
// → review → apply, mirroring the questions import flow with a focused review.

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onImported?: () => void;
}

type Phase = 'select' | 'review' | 'done';

export function TestImportDialog({ isOpen, onClose, onImported }: Props) {
  const logger = useLogger(WebComponentCode.WEB_UI, 'test-import');
  const [phase, setPhase] = useState<Phase>('select');
  const [fileName, setFileName] = useState('');
  const [sets, setSets] = useState<Record<string, unknown>[]>([]);
  const [preview, setPreview] = useState<QuestionDiffResult | null>(null);
  const [result, setResult] = useState<TestImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setPhase('select'); setFileName(''); setSets([]); setPreview(null);
    setResult(null); setBusy(false); setError(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFile = async (file: File) => {
    setError(null);
    setBusy(true);
    try {
      const parsed = JSON.parse(await file.text());
      const questionSets: unknown = Array.isArray(parsed) ? parsed : parsed?.questionSets;
      if (!Array.isArray(questionSets) || questionSets.length === 0) {
        throw new Error('File must contain a non-empty "questionSets" array');
      }
      setFileName(file.name);
      setSets(questionSets as Record<string, unknown>[]);
      logger.info('Previewing test import', { file: file.name, count: questionSets.length });
      const diff = await questionSetApi.previewImport(questionSets as Record<string, unknown>[]);
      setPreview(diff);
      setPhase('review');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read file');
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async () => {
    setBusy(true);
    setError(null);
    try {
      logger.info('Applying test import', { count: sets.length });
      const res = await questionSetApi.applyImport(sets);
      setResult(res);
      setPhase('done');
      onImported?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const applicable = preview ? preview.creates.length + preview.updates.length : 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Public Tests" size="lg">
      <div className="test-import">
        {error && <div className="test-error">{error}</div>}

        {phase === 'select' && (
          <div className="test-import-select">
            <p className="test-import-hint">
              Import test definitions exported from another environment. Questions must already be
              present here — import them first via the Questions page. Any test with missing
              questions will be flagged and skipped.
            </p>
            <label className="test-import-drop">
              <input
                type="file"
                accept=".json"
                disabled={busy}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
              />
              <span>{busy ? 'Reading…' : 'Choose a tests JSON file'}</span>
            </label>
          </div>
        )}

        {phase === 'review' && preview && (
          <div className="test-import-review">
            <div className="test-import-summary">
              <span className="ti-chip ti-create">{preview.creates.length} new</span>
              <span className="ti-chip ti-update">{preview.updates.length} updated</span>
              <span className="ti-chip ti-unchanged">{preview.unchanged.length} unchanged</span>
              {preview.errors.length > 0 && <span className="ti-chip ti-error">{preview.errors.length} error</span>}
              <span className="test-import-file">{fileName}</span>
            </div>

            <Bucket title="Errors — will be skipped" items={preview.errors} variant="error" showReason />
            <Bucket title="Will update" items={preview.updates} variant="update" showChanged />
            <Bucket title="New" items={preview.creates} variant="create" />
            <Bucket title="Unchanged" items={preview.unchanged} variant="unchanged" collapsedByDefault />

            <div className="test-import-actions">
              <button className="btn-secondary" onClick={reset} disabled={busy}>Choose another file</button>
              <button className="btn-primary" onClick={handleImport} disabled={busy || applicable === 0}>
                {busy ? 'Importing…' : applicable === 0 ? 'Nothing to import' : `Import ${applicable} test(s)`}
              </button>
            </div>
          </div>
        )}

        {phase === 'done' && result && (
          <div className="test-import-done">
            <div className="test-import-summary">
              <span className="ti-chip ti-create">{result.created} created</span>
              <span className="ti-chip ti-update">{result.updated} updated</span>
              <span className="ti-chip ti-unchanged">{result.unchanged} unchanged</span>
              {result.skipped > 0 && <span className="ti-chip">{result.skipped} skipped</span>}
              {result.errors > 0 && <span className="ti-chip ti-error">{result.errors} error</span>}
            </div>
            {result.results.filter(r => r.status === 'error').length > 0 && (
              <div className="test-import-bucket">
                <div className="test-import-bucket-title">Skipped (errors)</div>
                {result.results.filter(r => r.status === 'error').map(r => (
                  <div key={r.id} className="test-import-row test-import-row-error">
                    <span className="ti-label">{r.title}</span>
                    {r.reason && <span className="ti-reason">{r.reason}</span>}
                  </div>
                ))}
              </div>
            )}
            <div className="test-import-actions">
              <button className="btn-primary" onClick={handleClose}>Done</button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Bucket({
  title, items, variant, showReason, showChanged, collapsedByDefault,
}: {
  title: string;
  items: QuestionDiffItem[];
  variant: 'error' | 'update' | 'create' | 'unchanged';
  showReason?: boolean;
  showChanged?: boolean;
  collapsedByDefault?: boolean;
}) {
  const [open, setOpen] = useState(!collapsedByDefault);
  if (items.length === 0) return null;
  return (
    <div className="test-import-bucket">
      <button className="test-import-bucket-title" onClick={() => setOpen(o => !o)}>
        {open ? '▾' : '▸'} {title} ({items.length})
      </button>
      {open && items.map(it => (
        <div key={it.id} className={`test-import-row test-import-row-${variant}`}>
          <span className="ti-label">{it.label}</span>
          {showChanged && it.changedFields && it.changedFields.length > 0 && (
            <span className="ti-changed">Changed: {it.changedFields.join(', ')}</span>
          )}
          {showReason && it.skipReason && <span className="ti-reason">{it.skipReason}</span>}
        </div>
      ))}
    </div>
  );
}
